// src/components/results/PAInTGrid.tsx
import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { SignalLevel } from '../../lib/scoring'

interface PAInTCellProps {
  domain: 'P' | 'A' | 'In' | 'T'
  label: string
  signal: SignalLevel
  col: 0 | 1   // 0 = left, 1 = right
  row: 0 | 1   // 0 = top, 1 = bottom
}

const DOMAIN_DESCRIPTIONS: Record<string, Record<SignalLevel, string>> = {
  P: {
    'clear': 'Prolactin levels appear to be within normal range based on your responses.',
    'signalled': 'Your responses suggest possible prolactin activity. Elevated prolactin can suppress ovulation and affect libido and cycle regularity.',
    'strongly-signalled': 'Your responses strongly suggest prolactin involvement. Hyperprolactinemia is one of the most common treatable causes of cycle disruption and is identified with a single blood test.',
  },
  A: {
    'clear': 'Androgen indicators appear unremarkable based on your responses.',
    'signalled': 'Your responses suggest possible androgen activity — the pattern associated with PCOS and adrenal dysfunction.',
    'strongly-signalled': 'Your responses show a strong androgen signal. Jaw acne, hair changes, and irregular cycles together are a recognisable PCOS pattern that warrants a targeted hormonal panel.',
  },
  In: {
    'clear': 'Insulin resistance indicators appear low based on your responses.',
    'signalled': 'Your responses suggest possible insulin resistance. This is a metabolic pattern directly linked to cycle disruption and ovulation quality.',
    'strongly-signalled': 'Your responses show a strong insulin resistance signal. Central weight gain, afternoon energy crashes, and carbohydrate cravings together are a recognisable metabolic pattern.',
  },
  T: {
    'clear': 'Thyroid indicators appear unremarkable based on your responses.',
    'signalled': 'Your responses suggest possible thyroid involvement. Thyroid hormone affects every cell in the body — including cycle regularity, mood, hair, and energy.',
    'strongly-signalled': 'Your responses strongly suggest thyroid involvement. TSH and T4 should be part of any hormonal investigation given your response pattern.',
  },
}

const SIGNAL_CONFIG: Record<SignalLevel, { bars: number; color: string; label: string }> = {
  'clear':              { bars: 0, color: 'var(--th-rule)',      label: 'Clear' },
  'signalled':          { bars: 3, color: 'var(--th-pine)',      label: 'Signalled' },
  'strongly-signalled': { bars: 5, color: 'var(--th-vermilion)', label: 'Strong' },
}

function SignalBars({ signal }: { signal: SignalLevel }) {
  const { bars, color } = SIGNAL_CONFIG[signal]
  return (
    <div style={{ display: 'flex', gap: '3px', alignItems: 'flex-end', margin: '10px 0 8px' }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ delay: i * 0.05 + 0.2, duration: 0.18, ease: 'easeOut' }}
          style={{
            width: '6px',
            height: `${5 + i * 3}px`,
            background: i < bars ? color : 'var(--th-rule)',
            transformOrigin: 'bottom',
          }}
        />
      ))}
    </div>
  )
}

function PAInTCell({ domain, label, signal, col, row }: PAInTCellProps) {
  const [expanded, setExpanded] = useState(false)
  const { color, label: signalLabel } = SIGNAL_CONFIG[signal]

  return (
    <div
      onClick={() => setExpanded(!expanded)}
      style={{
        padding: '20px 20px 22px',
        borderRight: col === 0 ? '1px solid var(--th-rule)' : 'none',
        borderBottom: row === 0 ? '1px solid var(--th-rule)' : 'none',
        cursor: 'pointer',
        background: 'transparent',
      }}
    >
      {/* Domain key — mono, muted */}
      <div style={{
        fontFamily: 'var(--th-mono)',
        fontSize: '9.5px',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: 'var(--th-muted)',
        marginBottom: '6px',
      }}>
        {domain} · {label}
      </div>

      {/* Domain value — Fraunces 300, large, colored by signal */}
      <div style={{
        fontFamily: 'var(--th-serif)',
        fontWeight: 300,
        fontSize: '44px',
        lineHeight: 0.9,
        letterSpacing: '-0.03em',
        color: signal !== 'clear' ? color : 'var(--th-rule)',
      }}>
        {domain}
      </div>

      <SignalBars signal={signal} />

      {/* Signal label */}
      <div style={{
        fontFamily: 'var(--th-mono)',
        fontSize: '9.5px',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color,
        marginBottom: '6px',
      }}>
        {signalLabel}
      </div>

      {/* Toggle */}
      <div style={{
        fontFamily: 'var(--th-mono)',
        fontSize: '8px',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: 'var(--th-muted)',
      }}>
        {expanded ? '▲ Less' : '▼ Detail'}
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              marginTop: '10px',
              fontFamily: 'var(--th-serif)',
              fontWeight: 300,
              fontSize: '14px',
              lineHeight: 1.6,
              letterSpacing: '-0.005em',
              color: 'var(--th-ink-2)',
              overflow: 'hidden',
            }}
          >
            {DOMAIN_DESCRIPTIONS[domain][signal]}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  )
}

interface PAInTGridProps {
  signals: {
    prolactin: SignalLevel
    androgens: SignalLevel
    insulin: SignalLevel
    thyroid: SignalLevel
  }
}

export function PAInTGrid({ signals }: PAInTGridProps) {
  const cells = [
    { domain: 'P'  as const, label: 'Prolactin', signal: signals.prolactin, col: 0 as const, row: 0 as const },
    { domain: 'A'  as const, label: 'Androgens', signal: signals.androgens, col: 1 as const, row: 0 as const },
    { domain: 'In' as const, label: 'Insulin',   signal: signals.insulin,   col: 0 as const, row: 1 as const },
    { domain: 'T'  as const, label: 'Thyroid',   signal: signals.thyroid,   col: 1 as const, row: 1 as const },
  ]

  return (
    <section aria-label="Your PAInT Hormonal Profile">
      <span style={{
        fontFamily: 'var(--th-mono)',
        fontSize: '10.5px',
        fontWeight: 500,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: 'var(--th-muted)',
        display: 'block',
        marginBottom: '10px',
      }}>
        Hormonal Signals
      </span>
      <h3 style={{
        fontFamily: 'var(--th-serif)',
        fontWeight: 400,
        fontSize: '32px',
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
        color: 'var(--th-ink)',
        marginBottom: '8px',
      }}>
        Your PAInT Profile
      </h3>
      <p style={{
        fontFamily: 'var(--font-sans)',
        fontSize: '13px',
        color: 'var(--th-muted)',
        lineHeight: 1.6,
        marginBottom: '20px',
      }}>
        The four hormonal systems this assessment screens for. Tap any cell for detail.
      </p>

      {/* Data-strip: single outer ink border, cells share internal rule dividers, no gap */}
      <div style={{
        border: '1px solid var(--th-ink)',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
      }}>
        {cells.map(cell => (
          <PAInTCell key={cell.domain} {...cell} />
        ))}
      </div>
    </section>
  )
}
